import { Avatar, Dropdown, type MenuProps } from 'antd';
import clsx from 'clsx';
import DOMPurify from 'dompurify';
import { renderNotificationTag } from 'features/notification/constants';
import {
  formatNotificationDateTime,
  getNotificationImageUrl,
  getSenderInitial,
} from 'features/notification/helpers';
import { type TNotification } from 'features/notification/types';
import { currentUserSelector } from 'features/user/selectors';
import { useAppSelector } from 'lib/stores/hook';
import { memo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CheckReadOutlineIcon,
  MenuDotsOutlineIcon,
  TrashBinTrashOutlineIcon,
} from 'shared/components/AppIcon';

type Props = {
  data: TNotification;
  viewMode?: 'panel' | 'list-page';
  showTag?: boolean;
  onRead?: (id: string) => void;
  onDelete?: (id: string) => void;
  onNavigate?: () => void;
};

export const NotificationItem = memo(
  ({
    data,
    viewMode = 'list-page',
    showTag = true,
    onRead,
    onDelete,
    onNavigate,
  }: Props) => {
    const navigate = useNavigate();
    const currentUser = useAppSelector(currentUserSelector);
    const isPanelViewMode = viewMode === 'panel';

    const avatarInitial = getSenderInitial(
      data.sender_name || currentUser?.name_uppercase
    );

    const handleClickItem = useCallback(() => {
      if (!data.is_read) onRead?.(data.id);
      if (data.target_url) {
        navigate(data.target_url);
        onNavigate?.();
      }
    }, [data.is_read, data.id, data.target_url, onRead, navigate, onNavigate]);

    const items: MenuProps['items'] = [
      {
        key: 'read',
        label: (
          <div className="flex items-center gap-2 text-sm">
            <CheckReadOutlineIcon className="h-4 w-4 text-primary" />
            Đã đọc
          </div>
        ),
        onClick: () => onRead?.(data.id),
      },
      {
        key: 'delete',
        danger: true,
        label: (
          <div className="flex items-center gap-2 text-sm">
            <TrashBinTrashOutlineIcon className="h-4 w-4" />
            Xóa
          </div>
        ),
        onClick: () => onDelete?.(data.id),
      },
    ];

    return (
      <div className="flex items-start justify-between gap-3">
        <div
          className="flex min-w-0 flex-1 cursor-pointer items-start gap-4"
          onClick={handleClickItem}
          role="button"
        >
          <Avatar
            size={isPanelViewMode ? 36 : 48}
            src={getNotificationImageUrl(data.sender_avatar_id)}
            className="shrink-0"
          >
            {avatarInitial}
          </Avatar>

          <div className="min-w-0">
            <p
              className={clsx(
                'line-clamp-2 text-ellipsis [&_b]:font-bold [&_strong]:font-bold',
                data.is_read ? 'font-normal' : 'font-semibold',
                isPanelViewMode ? 'text-[13px]' : 'text-sm'
              )}
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(data.content),
              }}
            />
            <time
              dateTime={data.created_at}
              className="mt-1 block text-xs font-normal text-neutral-800"
            >
              {formatNotificationDateTime(data.created_at)}
            </time>
          </div>
        </div>

        {!showTag && isPanelViewMode ? null : (
          <div className="flex items-center justify-end gap-2">
            {showTag && renderNotificationTag(data.type_label)}

            {!isPanelViewMode && (
              <Dropdown
                menu={{ items }}
                trigger={['click']}
                placement="bottomRight"
                align={{
                  offset: [0, 8],
                }}
              >
                <button
                  type="button"
                  aria-label="Tùy chọn thông báo"
                  className="flex cursor-pointer items-center justify-center border-0 bg-transparent p-0"
                >
                  <MenuDotsOutlineIcon className="h-5 w-5 rotate-90 cursor-pointer text-neutral-500" />
                </button>
              </Dropdown>
            )}
          </div>
        )}
      </div>
    );
  }
);
